import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, FileText, Shield, AlertCircle, CheckCircle2, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_fc6c6161-2836-49b1-893b-3aa313732815/artifacts/x3f3fpsf_Cash-x_logo_mit_hintergrund-removebg-preview.png";

const ContractReviewSimple = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [contract, setContract] = useState(null);
  const [error, setError] = useState(null);

  const [acceptAgb, setAcceptAgb] = useState(false);
  const [acceptAvv, setAcceptAvv] = useState(false);
  const [acceptTse, setAcceptTse] = useState(false);
  const [typedName, setTypedName] = useState('');

  const [success, setSuccess] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    fetchActiveContract();
  }, []);

  const fetchActiveContract = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/contracts/active`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        navigate('/login');
        return;
      }

      if (!response.ok) {
        throw new Error('Vertrag konnte nicht geladen werden');
      }

      const data = await response.json();
      setContract(data);

      if (data.already_accepted) {
        toast.success('Vertrag bereits akzeptiert');
        navigate('/app');
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!acceptAgb || !acceptAvv || !acceptTse) {
      toast.error('Bitte akzeptieren Sie alle drei Vertragsteile');
      return;
    }

    if (!typedName.trim()) {
      toast.error('Bitte geben Sie Ihren Namen ein');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}/api/contracts/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          typed_name: typedName.trim(),
          accept_agb: acceptAgb,
          accept_avv: acceptAvv,
          accept_tse_annex: acceptTse,
          source: 'manual'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Fehler beim Akzeptieren');
      }

      const result = await response.json();
      toast.success('Vertrag erfolgreich akzeptiert!');
      setSuccess(true);
      setPdfUrl(result.pdf_url);

      setTimeout(() => {
        navigate('/app');
      }, 5000);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async () => {
    if (!pdfUrl) return;
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${BACKEND_URL}${pdfUrl}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) throw new Error('PDF konnte nicht geladen werden');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cashx_vertrag_${contract.version}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF heruntergeladen');
    } catch (err) {
      toast.error('PDF Download fehlgeschlagen');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Vertrag wird geladen...</p>
        </div>
      </div>
    );
  }

  if (error && !contract) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-lg w-full" data-testid="success-card">
          <CardHeader className="text-center">
            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-12 h-12 text-green-500" />
            </div>
            <CardTitle className="text-2xl">Vertrag erfolgreich akzeptiert! ✅</CardTitle>
            <CardDescription className="mt-2 text-base">
              Ihre digitale Unterschrift wurde rechtssicher gespeichert.
              {pdfUrl && <><br />Ein PDF-Archiv wurde erstellt und steht zum Download bereit.</>}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {pdfUrl && (
              <>
                <Alert>
                  <Download className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Ihr signierter Vertrag:</strong> Sie können das PDF jetzt herunterladen und archivieren.
                  </AlertDescription>
                </Alert>
                <Button
                  onClick={downloadPdf}
                  variant="default"
                  size="lg"
                  className="w-full"
                  data-testid="btn-download-pdf"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Vertrag als PDF herunterladen
                </Button>
              </>
            )}
            <Button
              onClick={() => navigate('/app')}
              variant={pdfUrl ? "outline" : "default"}
              size="lg"
              className="w-full"
              data-testid="btn-goto-dashboard"
            >
              Weiter zum Dashboard →
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Automatische Weiterleitung in 5 Sekunden...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isFormValid = acceptAgb && acceptAvv && acceptTse && typedName.trim().length > 0;

  return (
    <div className="min-h-screen bg-background" data-testid="contract-review-page">
      <div className="border-b bg-card">
        <div className="max-w-5xl mx-auto p-6">
          <div className="flex items-center gap-4">
            <img
              src={LOGO_URL}
              alt="CashX Logo"
              className="h-12 w-auto"
            />
            <div>
              <h1 className="text-2xl font-bold">Vertragsannahme erforderlich</h1>
              <p className="text-sm text-muted-foreground">
                {contract.title} - Version {contract.version}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Wichtig:</strong> Bitte lesen Sie die folgenden Vertragsdokumente sorgfältig durch.
            Ihre digitale Unterschrift wird rechtssicher gespeichert und ein PDF-Archiv wird erstellt.
          </AlertDescription>
        </Alert>

        <Card data-testid="contract-tabs-card">
          <CardHeader>
            <CardTitle>Vertragsdokumente</CardTitle>
            <CardDescription>
              Bitte lesen Sie alle drei Dokumente vollständig
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="agb" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="agb" data-testid="tab-agb">
                  <FileText className="w-4 h-4 mr-2" />
                  AGB
                </TabsTrigger>
                <TabsTrigger value="avv" data-testid="tab-avv">
                  <Shield className="w-4 h-4 mr-2" />
                  AVV (DSGVO)
                </TabsTrigger>
                <TabsTrigger value="tse" data-testid="tab-tse">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  TSE Annex
                </TabsTrigger>
              </TabsList>

              <div className="mt-6 max-h-[500px] overflow-y-auto p-4 border rounded-lg bg-muted/30">
                <TabsContent value="agb" data-testid="contract-agb-content">
                  <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
                    {contract.documents.agb_md}
                  </div>
                </TabsContent>

                <TabsContent value="avv" data-testid="contract-avv-content">
                  <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
                    {contract.documents.avv_md}
                  </div>
                </TabsContent>

                <TabsContent value="tse" data-testid="contract-tse-content">
                  <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap">
                    {contract.documents.tse_annex_md}
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </CardContent>
        </Card>

        <Card data-testid="acceptance-form-card">
          <CardHeader>
            <CardTitle>Vertragsannahme & Digitale Unterschrift</CardTitle>
            <CardDescription>
              Bitte akzeptieren Sie alle Dokumente und unterschreiben Sie digital
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-start space-x-3" data-testid="checkbox-agb-container">
                <Checkbox
                  id="accept-agb"
                  checked={acceptAgb}
                  onCheckedChange={setAcceptAgb}
                  data-testid="checkbox-agb"
                />
                <Label
                  htmlFor="accept-agb"
                  className="text-sm font-normal leading-snug cursor-pointer"
                >
                  Ich habe die <strong>Allgemeinen Geschäftsbedingungen (AGB)</strong> gelesen und akzeptiere sie
                </Label>
              </div>

              <div className="flex items-start space-x-3" data-testid="checkbox-avv-container">
                <Checkbox
                  id="accept-avv"
                  checked={acceptAvv}
                  onCheckedChange={setAcceptAvv}
                  data-testid="checkbox-avv"
                />
                <Label
                  htmlFor="accept-avv"
                  className="text-sm font-normal leading-snug cursor-pointer"
                >
                  Ich habe die <strong>Auftragsverarbeitungsvereinbarung (AVV / DSGVO)</strong> gelesen und akzeptiere sie
                </Label>
              </div>

              <div className="flex items-start space-x-3" data-testid="checkbox-tse-container">
                <Checkbox
                  id="accept-tse"
                  checked={acceptTse}
                  onCheckedChange={setAcceptTse}
                  data-testid="checkbox-tse"
                />
                <Label
                  htmlFor="accept-tse"
                  className="text-sm font-normal leading-snug cursor-pointer"
                >
                  Ich habe den <strong>TSE Annex (Fiskaly / KassenSichV)</strong> gelesen und akzeptiere ihn
                </Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="typed-name">Digitale Unterschrift (Name eingeben)</Label>
              <Input
                id="typed-name"
                type="text"
                placeholder="z.B. Max Mustermann"
                value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                data-testid="input-typed-name"
              />
              <p className="text-xs text-muted-foreground">
                Mit Ihrer Unterschrift bestätigen Sie die Vertragsannahme rechtsgültig
              </p>
            </div>

            <Button
              onClick={handleAccept}
              disabled={!isFormValid || submitting}
              className="w-full"
              size="lg"
              data-testid="btn-accept-contract"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Wird verarbeitet...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Vertrag digital unterschreiben
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ContractReviewSimple;
